/**
 * 错音复习队列（Midoo 教学法的第四步：高频错误回流）
 * ============================================================
 * Midoo 的教学循环是「学 → 用 → 纠 → 回流」：
 *   练错的音不能练完就丢，要沉淀下来，下次优先重练。
 *
 * 这个模块负责记录与调度这些错音。设计要点：
 *
 *   1. **只记录真的被判错的**：后端在缺少粤拼时会退化成置信度自评，
 *      那种情况下 tonAccuracy 不代表对错，绝不能因此把用户"记账"。
 *      所以只有 compared === true 且准确度低于阈值的音节才会入队。
 *
 *   2. **按「字 + 期望声调」去重**：同一个字读错不同调是两回事，
 *      分开计数才能给出准确的重练建议。
 *
 *   3. **简单的优先级**：错误次数越多、最近越常错，排得越前。
 *      这里不引入完整的 SRS 算法（SM-2/FSRS），因为对"声调纠错"
 *      这个窄场景，频次 + 时间衰减已经足够，且完全可在本地跑。
 */

const QUEUE_KEY = 'learn_cantonese_review_queue';

/** 低于这个准确度才算"读错" */
export const ERROR_THRESHOLD = 70;

/** 队列上限，避免无限增长 */
const MAX_ITEMS = 200;

/** 连续答对多少次后移出队列 */
const GRADUATE_STREAK = 2;

/**
 * 读取队列
 * @returns {Array<Object>}
 */
function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn('[reviewQueue] 读取失败:', e?.message);
    return [];
  }
}

function writeQueue(items) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS)));
  } catch (e) {
    console.warn('[reviewQueue] 保存失败:', e?.message);
  }
}

/**
 * 生成条目唯一键：同一个字读错不同声调要分开计
 * @param {string} character
 * @param {number|null} expectedTone
 * @returns {string}
 */
function itemKey(character, expectedTone) {
  return `${character}|${expectedTone ?? '?'}`;
}

/**
 * 从一次评测结果中提取错音并入队
 *
 * @param {Object} params
 * @param {Array}  params.syllables        - audioAnalysis.detectedTones
 * @param {boolean} params.toneComparisonEnabled - 是否真的做了声调对比
 * @param {string} [params.cantonese]      - 原句，便于复习时展示上下文
 * @param {string} [params.jyutping]       - 原句粤拼
 * @param {string} [params.lessonId]       - 来源课程
 * @returns {Array<Object>} 本次新增或更新的错音条目
 */
export function recordErrors({
  syllables,
  toneComparisonEnabled,
  cantonese = '',
  jyutping = '',
  lessonId = null,
} = {}) {
  // 没做声调对比时，准确度是置信度伪装出来的，不能据此记错
  if (!toneComparisonEnabled) {
    return [];
  }
  if (!Array.isArray(syllables) || syllables.length === 0) {
    return [];
  }

  const queue = readQueue();
  const touched = [];

  syllables.forEach((s) => {
    if (!s || s.compared !== true) return;
    if (typeof s.toneAccuracy !== 'number' || Number.isNaN(s.toneAccuracy)) return;
    if (s.toneAccuracy >= ERROR_THRESHOLD) return;
    if (!s.character || s.character === '?') return;

    const key = itemKey(s.character, s.expectedTone);
    const existing = queue.find((it) => it.key === key);

    if (existing) {
      existing.count += 1;
      existing.lastAccuracy = s.toneAccuracy;
      existing.lastAt = Date.now();
      existing.streak = 0;
      existing.actualTone = s.tone ?? existing.actualTone;
      if (cantonese) existing.cantonese = cantonese;
      if (jyutping) existing.jyutping = jyutping;
      touched.push(existing);
    } else {
      const item = {
        key,
        character: s.character,
        expectedTone: s.expectedTone ?? null,
        actualTone: s.tone ?? null,
        lastAccuracy: s.toneAccuracy,
        count: 1,
        streak: 0,
        cantonese,
        jyutping,
        lessonId,
        firstAt: Date.now(),
        lastAt: Date.now(),
      };
      queue.push(item);
      touched.push(item);
    }
  });

  writeQueue(queue);
  return touched;
}

/**
 * 从一次评测结果中提取「读对了」的音节，用于把已掌握的字移出队列
 *
 * @param {Object} params
 * @param {Array}  params.syllables
 * @param {boolean} params.toneComparisonEnabled
 * @returns {string[]} 已移出队列的 key
 */
export function recordSuccesses({ syllables, toneComparisonEnabled } = {}) {
  if (!toneComparisonEnabled || !Array.isArray(syllables)) return [];

  const queue = readQueue();
  const removedKeys = [];

  syllables.forEach((s) => {
    if (!s || s.compared !== true) return;
    if (typeof s.toneAccuracy !== 'number') return;
    if (s.toneAccuracy < 85) return; // 只有读得相当好才算"掌握"

    const key = itemKey(s.character, s.expectedTone);
    const item = queue.find((it) => it.key === key);
    if (!item) return;

    item.streak = (item.streak || 0) + 1;
    item.lastAt = Date.now();

    // 连续答对若干次才移出，避免偶然读对就清账
    if (item.streak >= GRADUATE_STREAK) {
      removedKeys.push(key);
    }
  });

  const remaining = queue.filter((it) => !removedKeys.includes(it.key));
  writeQueue(remaining);
  return removedKeys;
}

/**
 * 取得待复习条目，按优先级排序
 *
 * 优先级 = 错误次数权重 + 时间衰减权重
 * （越常错、越久没练的越靠前）
 *
 * @param {number} [limit] - 最多返回多少条
 * @returns {Array<Object>}
 */
export function getReviewItems(limit = 50) {
  const queue = readQueue();
  const now = Date.now();
  const DAY = 24 * 60 * 60 * 1000;

  const scored = queue.map((it) => {
    const daysSince = Math.max(0, (now - (it.lastAt || now)) / DAY);
    // 时间越久，优先级越高（上限 7 天，避免无限增长）
    const recencyBoost = Math.min(daysSince, 7) / 7;
    const score = (it.count || 1) * 2 + recencyBoost * 1.5;
    return { ...it, _score: score };
  });

  scored.sort((a, b) => b._score - a._score);
  return scored.slice(0, limit);
}

/**
 * 队列统计
 * @returns {{ total: number, characters: number }}
 */
export function getReviewStats() {
  const queue = readQueue();
  return {
    total: queue.length,
    characters: new Set(queue.map((it) => it.character)).size,
  };
}

/**
 * 手动移除一条
 * @param {string} key
 */
export function removeReviewItem(key) {
  writeQueue(readQueue().filter((it) => it.key !== key));
}

/** 清空队列 */
export function clearReviewQueue() {
  try {
    localStorage.removeItem(QUEUE_KEY);
  } catch (e) {
    console.warn('[reviewQueue] 清空失败:', e?.message);
  }
}
